import {useEffect, useRef} from 'react'
import {probeConnectHeartbeat} from '../services/connectEndpointTest/probeConnectHeartbeat'
import {isAgentNetworkTelemetryForceDisabled} from '../services/agentNetworkTelemetry/config'
import {sendAgentNetworkPing} from '../services/agentNetworkTelemetry/api'
import {
  setWebRtcTelemetryContactId,
  setWebRtcTelemetrySender,
} from '../services/agentNetworkTelemetry/webrtcTelemetryBridge'
import {enqueueWebRtcSamples} from '../services/agentNetworkTelemetry/telemetryOutbox'
import {
  audioDeviceChangeDetail,
  getCcpSnapshotAgeMs,
  reportCcpEvent,
  setCcpTelemetryArmed,
} from '../services/agentNetworkTelemetry/ccpEvents'
import {
  getAgentTelemetryRuntimeConfig,
  subscribeAgentTelemetryRuntimeConfig,
} from '../services/agentNetworkTelemetry/runtimeConfig'

export type UseAgentNetworkTelemetryArgs = {
  /** Master tab only — observers must not send. */
  isMasterTab: boolean | null
  /** CCP Streams session logged in. */
  isLoggedIn: boolean
  agentStatus: string
  contactId?: string | null
  /** CCP mute flag. Omit until the agent snapshot exists. */
  muted?: boolean | null
}

const FIRST_TICK_MAX_DELAY_MS = 30000
const JITTER_FRACTION = 0.2

function readVisibility(): 'visible' | 'hidden' {
  if (typeof document === 'undefined') return 'visible'
  return document.visibilityState === 'hidden' ? 'hidden' : 'visible'
}

/** ±20% so interpreter tabs do not share one clock. */
function jitteredInterval(intervalMs: number): number {
  const spread = intervalMs * JITTER_FRACTION
  return Math.max(0, Math.round(intervalMs + (Math.random() * 2 - 1) * spread))
}

/**
 * Master tab + CCP logged in. Cadence and the on/off switch come from InterpreterHub
 * (`telemetry:config`): 30s idle, 10s while contactId is set, unless the server says otherwise.
 * First tick waits a random 0–30s. Each later tick is jittered ±20%.
 * A down hub skips the sample. Hidden-tab gaps are marked throttled on the server.
 */
export function useAgentNetworkTelemetry(args: UseAgentNetworkTelemetryArgs): void {
  const {isMasterTab, isLoggedIn, agentStatus, contactId, muted} = args

  const agentStatusRef = useRef(agentStatus)
  const contactIdRef = useRef(contactId)
  const mutedRef = useRef(muted)
  const inFlightRef = useRef(false)

  agentStatusRef.current = agentStatus
  contactIdRef.current = contactId
  mutedRef.current = muted

  useEffect(() => {
    setWebRtcTelemetryContactId(contactId ?? null)
  }, [contactId])

  useEffect(() => {
    if (isAgentNetworkTelemetryForceDisabled()) return
    if (isMasterTab !== true) return
    if (!isLoggedIn) return

    setWebRtcTelemetrySender((samples, activeContactId) => {
      const config = getAgentTelemetryRuntimeConfig()
      if (!config?.enabled) return
      enqueueWebRtcSamples({
        contactId: activeContactId,
        samples: samples.map((sample) => ({
          atMs: sample.atMs,
          direction: sample.direction,
          packetsLost: sample.packetsLost,
          packetsCount: sample.packetsCount,
          audioLevel: sample.audioLevel,
          jitterMs: sample.jitterMs,
          rttMs: sample.rttMs,
        })),
      })
    })
    setCcpTelemetryArmed(true)

    return () => {
      setWebRtcTelemetrySender(null)
      setCcpTelemetryArmed(false)
    }
  }, [isMasterTab, isLoggedIn])

  useEffect(() => {
    if (isMasterTab !== true || !isLoggedIn || !contactId) return
    const onVisibility = () => {
      try {
        reportCcpEvent(document.visibilityState === 'hidden' ? 'tab_hidden' : 'tab_visible', {
          contactId,
        })
      } catch {
        /* telemetry only */
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [isMasterTab, isLoggedIn, contactId])

  useEffect(() => {
    if (isMasterTab !== true || !isLoggedIn) return
    const media = navigator.mediaDevices
    if (!media?.addEventListener) return
    const onDeviceChange = () => {
      void audioDeviceChangeDetail()
        .then((detail) => {
          reportCcpEvent('audio_device_change', {
            contactId: contactIdRef.current,
            detail,
          })
        })
        .catch(() => {
          /* telemetry only */
        })
    }
    media.addEventListener('devicechange', onDeviceChange)
    return () => media.removeEventListener('devicechange', onDeviceChange)
  }, [isMasterTab, isLoggedIn])

  useEffect(() => {
    if (isAgentNetworkTelemetryForceDisabled()) return
    if (isMasterTab !== true) return
    if (!isLoggedIn) return

    let cancelled = false
    let started = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
    }

    const tick = async () => {
      if (cancelled || inFlightRef.current) return
      const config = getAgentTelemetryRuntimeConfig()
      if (!config?.enabled) return

      inFlightRef.current = true
      try {
        const probe = await probeConnectHeartbeat()
        if (cancelled) return
        const latest = getAgentTelemetryRuntimeConfig()
        if (!latest?.enabled) return

        await sendAgentNetworkPing({
          clientSentAtMs: Date.now(),
          connectInstance: probe.instance
            ? {
                latencyMs: probe.instance.latencyMs,
                success: probe.instance.success,
                endpoint: probe.instance.endpoint,
                error: probe.instance.error,
              }
            : null,
          connectRegion: probe.region
            ? {
                latencyMs: probe.region.latencyMs,
                success: probe.region.success,
                endpoint: probe.region.endpoint,
                error: probe.region.error,
              }
            : null,
          status: agentStatusRef.current || 'Unknown',
          contactId: contactIdRef.current || null,
          visibility: readVisibility(),
          ccpSnapshotAgeMs: getCcpSnapshotAgeMs(),
          muted: mutedRef.current ?? null,
        })
      } catch {
        // A missing Influx point is the signal. Do not buffer or retry.
      } finally {
        inFlightRef.current = false
      }
    }

    const arm = (delayMs: number) => {
      clearTimer()
      timer = setTimeout(() => {
        void run()
      }, delayMs)
    }

    const run = async () => {
      const config = getAgentTelemetryRuntimeConfig()
      if (cancelled || !config?.enabled) return

      const startedAt = Date.now()
      await tick()
      if (cancelled) return

      const latest = getAgentTelemetryRuntimeConfig()
      if (!latest?.enabled) return
      const base = contactIdRef.current ? latest.activeIntervalMs : latest.idleIntervalMs
      const wait = Math.max(0, jitteredInterval(base) - (Date.now() - startedAt))
      arm(wait)
    }

    const startIfEnabled = () => {
      if (cancelled || started) return
      const config = getAgentTelemetryRuntimeConfig()
      if (!config?.enabled) return
      started = true
      arm(Math.round(Math.random() * FIRST_TICK_MAX_DELAY_MS))
    }

    const unsubscribe = subscribeAgentTelemetryRuntimeConfig(() => {
      if (cancelled) return
      const config = getAgentTelemetryRuntimeConfig()
      if (!config?.enabled) {
        started = false
        clearTimer()
        return
      }
      startIfEnabled()
    })

    startIfEnabled()

    return () => {
      cancelled = true
      clearTimer()
      unsubscribe()
      inFlightRef.current = false
    }
  }, [isMasterTab, isLoggedIn])
}
